import { useTranslation } from 'react-i18next';
import type { DisplayDocument } from '../../types/agent';
import { formatSize, type Attachment } from '../../hooks/useAttachments';

const KIND_TAG: Record<string, string> = { pdf: 'PDF', docx: 'DOC', pptx: 'PPT', xlsx: 'XLS', csv: 'CSV', text: 'TXT', image: 'IMG' };

function kindOf(name: string, kind?: string): string {
  if (kind && KIND_TAG[kind]) return KIND_TAG[kind];
  const ext = name.includes('.') ? name.split('.').pop()!.toLowerCase() : '';
  return KIND_TAG[{ pdf: 'pdf', docx: 'docx', pptx: 'pptx', xlsx: 'xlsx', xlsm: 'xlsx', csv: 'csv', tsv: 'csv', png: 'image', jpg: 'image', jpeg: 'image', webp: 'image', gif: 'image' }[ext] ?? 'text'] ?? 'TXT';
}

/** Composer chips (2026-09-10): one per picked file — uploading / ready (pages, size) / error — with a remove control. */
export function AttachmentChips({ items, onRemove }: { items: Attachment[]; onRemove: (localId: string) => void }) {
  const { t } = useTranslation();
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="attachment-chips">
      {items.map((a) => {
        const error = a.status === 'error';
        const detail = error ? (a.error?.startsWith('attachments.') ? t(a.error) : a.error) : a.status === 'uploading' ? t('attachments.uploading') : a.status === 'processing' ? t('attachments.reading') : summaryDetail(a, t);
        return (
          <div
            key={a.localId}
            title={detail ?? undefined}
            className={`flex max-w-full items-center gap-1.5 rounded-sm border px-2 py-[3px] text-[11px] ${error ? 'border-danger/30 bg-danger-bg text-danger' : 'border-line-soft bg-accent-bg text-accent-dark'}`}
          >
            <span className="shrink-0 font-mono text-[9px] font-semibold opacity-70">{kindOf(a.name, a.summary?.kind)}</span>
            <span className="min-w-0 truncate">{a.name}</span>
            {(a.status === 'uploading' || a.status === 'processing') && <span className="h-2.5 w-2.5 shrink-0 animate-spin rounded-full border border-current border-t-transparent" aria-label={detail ?? ''} />}
            {!error && a.status === 'ready' && <span className="shrink-0 font-mono text-[10px] opacity-70">{detail}</span>}
            <button type="button" onClick={() => onRemove(a.localId)} aria-label={t('attachments.remove', { name: a.name })} className="shrink-0 pl-0.5 text-[12px] leading-none opacity-70 hover:opacity-100">
              ×
            </button>
          </div>
        );
      })}
    </div>
  );
}

function summaryDetail(a: Attachment, t: (k: string, o?: Record<string, unknown>) => string): string {
  const s = a.summary;
  if (!s) return formatSize(a.size);
  const parts = [formatSize(a.size)];
  if (s.page_count > 1) parts.push(t('attachments.pages', { count: s.page_count }));
  if (s.ocr_pages) parts.push(t('attachments.ocr', { count: s.ocr_pages }));
  // A PDF or an image is read as pages, not as its text layer: no text is not a problem there.
  if (s.chars === 0 && s.kind !== 'image' && s.kind !== 'pdf') parts.push(t('attachments.noText'));
  else if (s.truncated) parts.push(t('attachments.truncated'));
  return parts.join(' · ');
}

/** Read-only chips under a sent user message (history and live). */
export function DocumentChips({ documents }: { documents: DisplayDocument[] }) {
  const { t } = useTranslation();
  if (!documents.length) return null;
  return (
    <div className="mt-1.5 flex flex-wrap justify-end gap-1" data-testid="document-chips">
      {documents.map((d) => (
        <span key={d.document_id} title={`${formatSize(d.size_bytes)}${d.page_count > 1 ? ` · ${t('attachments.pages', { count: d.page_count })}` : ''}`} className="flex max-w-full items-center gap-1 rounded-sm border border-line-soft bg-white/70 px-1.5 py-[2px] text-[11px] text-muted">
          <span className="font-mono text-[9px] font-semibold opacity-70">{kindOf(d.filename, d.kind)}</span>
          <span className="min-w-0 truncate">{d.filename}</span>
        </span>
      ))}
    </div>
  );
}
